import React, { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { format, parseISO } from 'date-fns';
import EmptyView from '@/shared/components/EmptyView';
import { InvoiceData } from '@/shared/types/invoiceTypes';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Button } from '@/shared/ui/button';
import { ChevronLeft, ChevronRight, Loader2, Plus, Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { subMonths } from 'date-fns';
import { useCreateInvoiceInZoho } from '../services/invoices';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { formatNumber } from '@/shared/utils/format-utils';

export function Invoices({
    selectedRegionCode,
    invoices,
    isLoading,
    error,
    hasPermission,
    refetch,
    onBillingPeriodChange
}: {
    selectedRegionCode: string | undefined,
    invoices: InvoiceData[],
    isLoading: boolean,
    error: Error | null,
    hasPermission: boolean,
    refetch: () => void,
    onBillingPeriodChange?: (billingPeriod: string) => void
}) {
    const billingPeriods: { billingPeriod: string, label: string }[] = useMemo(() => {
        return [...new Array(6)].map((_, i) => subMonths(new Date(), i + 1).toISOString().split('T')[0]).map((billingPeriod) => ({
            billingPeriod: billingPeriod,
            label: format(parseISO(billingPeriod), 'MMMM yyyy'),
        }));
    }, []);

    const [selectedBillingPeriod, setSelectedBillingPeriod] = useState<{ billingPeriod: string, label: string } | undefined>(billingPeriods[0]);
    const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState<number>(0);

    const createInvoiceInZoho = useCreateInvoiceInZoho();

    React.useEffect(() => {
        if (!billingPeriods.length) {
            setSelectedBillingPeriod(undefined);
            return;
        }

        if (!selectedBillingPeriod || !billingPeriods.some((period) => period.billingPeriod === selectedBillingPeriod.billingPeriod)) {
            const defaultBillingPeriod = billingPeriods[0];
            setSelectedBillingPeriod(defaultBillingPeriod);
            onBillingPeriodChange?.(defaultBillingPeriod.billingPeriod);
        }
    }, [billingPeriods, onBillingPeriodChange, selectedBillingPeriod]);

    React.useEffect(() => {
        if (!invoices?.length) {
            setCurrentInvoiceIndex(0);
            return;
        }

        setCurrentInvoiceIndex((prevIndex) => Math.min(prevIndex, invoices.length - 1));
    }, [invoices]);

    const selectedInvoice: InvoiceData | null = useMemo(() => {
        if (!invoices?.length) return null;
        return invoices[currentInvoiceIndex] ?? null;
    }, [invoices, currentInvoiceIndex]);

    const handleCreateInvoice = async () => {
        if (!selectedInvoice?.organization?.organizationId || !selectedBillingPeriod?.billingPeriod) {
            showError(ERROR.INVOICE.MISSING_REQUIRED_FIELDS);
            return;
        }
        try {
            const invoiceData = await createInvoiceInZoho.mutateAsync({ organizationId: selectedInvoice.organization.organizationId, yearMonth: selectedBillingPeriod.billingPeriod });
            if (!invoiceData.invoiceNumber || !invoiceData.invoiceId) {
                showError(ERROR.INVOICE.CREATE_IN_ZOHO_FAILED);
                return;
            }
            showSuccess(SUCCESS.INVOICE.CREATED_IN_ZOHO.replace('{{invoiceNumber}}', invoiceData.invoiceNumber).replace('{{invoiceId}}', invoiceData.invoiceId));
            alert(SUCCESS.INVOICE.CREATED_IN_ZOHO.replace('{{invoiceNumber}}', invoiceData.invoiceNumber).replace('{{invoiceId}}', invoiceData.invoiceId));
        } catch (error) {
            showErrorWithApiError(error, ERROR.INVOICE.CREATE_IN_ZOHO_FAILED);
        }
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
                <div>
                    <h1 className="text-3xl font-bold">Invoice Preview - {selectedBillingPeriod?.label}</h1>
                    {selectedRegionCode && (
                        <p className="text-lg">
                            Invoice preview for <span className="font-bold text-blue-500">{selectedRegionCode}</span> datapool
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {billingPeriods.length > 1 && (
                        <Select value={selectedBillingPeriod?.billingPeriod} onValueChange={(value) => {
                            const nextBillingPeriod = billingPeriods.find((billingPeriod) => billingPeriod.billingPeriod === value) ?? billingPeriods[0];
                            setSelectedBillingPeriod(nextBillingPeriod);
                            onBillingPeriodChange?.(nextBillingPeriod.billingPeriod);
                        }}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select billing period" />
                            </SelectTrigger>
                            <SelectContent>
                                {billingPeriods.map((billingPeriod) => (
                                    <SelectItem key={billingPeriod.billingPeriod} value={billingPeriod.billingPeriod}>
                                        {billingPeriod.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <p className="text-sm">Loading Invoice Data...</p>
                </div>
            ) : error ? (
                <EmptyView
                    icon={Receipt}
                    title="Error Loading Invoice Data"
                    description="There was an error loading the invoice data. Please try again."
                    actionLabel="Retry"
                    onAction={() => refetch()}
                />
            ) : selectedInvoice ? (
                <Card>
                    <CardHeader className="p-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <CardTitle>{selectedInvoice.organization?.name}</CardTitle>
                            <div className="flex items-center gap-2">
                                {invoices.length > 1 && (
                                    <>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setCurrentInvoiceIndex((prevIndex) => Math.max(0, prevIndex - 1))}
                                            disabled={currentInvoiceIndex === 0 || isLoading}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <p className="text-sm border border-border p-2 rounded-md">
                                            <span className="font-bold">{currentInvoiceIndex + 1}</span> / {invoices.length}
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setCurrentInvoiceIndex((prevIndex) => Math.min(invoices.length - 1, prevIndex + 1))}
                                            disabled={currentInvoiceIndex === invoices.length - 1 || isLoading}
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </>
                                )}
                                {handleCreateInvoice && hasPermission && (
                                    <Button
                                        onClick={handleCreateInvoice}
                                        className="gap-2"
                                        disabled={!selectedInvoice?.items?.length || isLoading || createInvoiceInZoho.isPending}
                                    >
                                        {createInvoiceInZoho.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                        Create in Zoho
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 space-y-4 pt-0">
                        {selectedInvoice.items && selectedInvoice.items.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border">
                                <Table className="w-full border-collapse">
                                    <TableHeader>
                                        <TableRow className="bg-muted">
                                            <TableHead className="text-left py-3 px-4 font-medium font-bold">#</TableHead>
                                            <TableHead className="text-left py-3 px-4 font-medium font-bold">Sku</TableHead>
                                            <TableHead className="text-left py-3 px-4 font-medium font-bold">Item & Description</TableHead>
                                            <TableHead className="text-left py-3 px-4 font-medium font-bold">Qty</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {selectedInvoice?.items?.map((item, index) => (
                                            <TableRow key={item.id} className="border-b hover:bg-muted/50">
                                                <TableCell className="py-3 px-4 font-medium">{index + 1}</TableCell>
                                                <TableCell className="py-3 px-4 font-medium">{item.sku}</TableCell>
                                                <TableCell className="py-3 px-4">
                                                    <div className="space-y-1">
                                                        <div className="font-medium">{item.itemDescription}</div>
                                                        {item.details && <div className="text-sm whitespace-pre-line">{item.details}</div>}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 px-4">{formatNumber(item.quantity)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <EmptyView
                                icon={Receipt}
                                size="medium"
                                title="No Billing Data"
                                description="No billing data available for the selected invoice."
                            />
                        )}
                    </CardContent>
                </Card >
            ) : (
                <EmptyView
                    icon={Receipt}
                    title="No Billing Data"
                    description="No billing data available for the selected month."
                />
            )}
        </div>
    );
}